import { useTranslation } from 'react-i18next';

import type { SignatureSettings } from '@emr/shared';

import { cssFontFamilyFor } from './fonts.ts';

/** Fixed render box for a drawn mark's `viewBox` — arbitrary units, matching the aspect ratio
 * `SignatureCanvas.tsx` captures strokes against (its own drawing surface is `aspect-[3/1]`, the
 * same 300:100 ratio), so a mark drawn wide-and-short does not get squashed differently between the
 * drawing surface and every place it is later displayed. Exported so `SignatureCanvas.tsx` builds
 * its own live in-progress preview from the identical function rather than a second copy of it. */
export const SIGNATURE_VIEW_WIDTH = 300;
export const SIGNATURE_VIEW_HEIGHT = 100;
const VIEW_WIDTH = SIGNATURE_VIEW_WIDTH;
const VIEW_HEIGHT = SIGNATURE_VIEW_HEIGHT;

export function strokesToPath(
  strokes: readonly (readonly (readonly [number, number])[])[],
): string {
  return strokes
    .map((stroke) => {
      if (stroke.length === 0) return '';
      const [firstX, firstY] = stroke[0] as [number, number];
      const rest = stroke
        .slice(1)
        .map(([x, y]) => `L ${String(x * VIEW_WIDTH)} ${String(y * VIEW_HEIGHT)}`)
        .join(' ');
      return `M ${String(firstX * VIEW_WIDTH)} ${String(firstY * VIEW_HEIGHT)} ${rest}`;
    })
    .filter((segment) => segment.length > 0)
    .join(' ');
}

/**
 * Renders one signature mark — drawn strokes as an inline SVG path, or adopted-font text — from a
 * `SignatureSettings`-shaped value. The single renderer for both the live "your current mark"
 * preview (`SignaturePage.tsx`) and a signed note's frozen snapshot (`NoteCard.tsx`): one component
 * reading one shape, so the two can never render a mark differently from each other.
 *
 * `settings` accepts `null` (never set) and a settings object whose `method` may itself be `null`
 * (a signature line was set before any mark was ever drawn/adopted) — both render the same "no mark
 * on file" placeholder rather than a blank box that reads as a rendering bug.
 */
export function SignatureMark({
  settings,
  className,
}: {
  readonly settings: SignatureSettings | null;
  readonly className?: string;
}) {
  const { t } = useTranslation('signature');

  if (settings === null || settings.method === null) {
    return (
      <p className={`m-0 text-sm text-ink-muted italic ${className ?? ''}`}>
        {t('mark.noMarkOnFile')}
      </p>
    );
  }

  if (settings.method === 'drawn') {
    const strokes = settings.drawnStrokes ?? [];
    if (strokes.length === 0) {
      return (
        <p className={`m-0 text-sm text-ink-muted italic ${className ?? ''}`}>
          {t('mark.noMarkOnFile')}
        </p>
      );
    }
    return (
      <svg
        viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(VIEW_HEIGHT)}`}
        role="img"
        aria-label={t('mark.drawnAriaLabel')}
        className={className ?? 'h-16 w-full max-w-xs'}
      >
        <path
          d={strokesToPath(strokes)}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-ink"
        />
      </svg>
    );
  }

  const fontFamily = settings.fontName !== null ? cssFontFamilyFor(settings.fontName) : 'cursive';
  return (
    <p
      className={className ?? 'm-0 text-3xl text-ink [overflow-wrap:anywhere]'}
      style={{ fontFamily }}
    >
      {settings.fontText ?? ''}
    </p>
  );
}
